import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';

interface DesignsInCollectionGridProps {
  data: {
    data: Array<{
      application: string;
      available_on_resources: boolean;
      b_trade_sync: string;
      code: string;
      collection: string;
      colour: string;
      design: string;
      diameter: string;
      id: string;
      is_published: boolean;
      item_group: string;
      primaryImage: string;
      primaryImageUrl: string;
      range: string;
      sku: string;
      web_colour: string;
      web_size: string;
      width: string;
      length_cm: string;
    }>;
    filters: {
      application: string[];
    };
    return_item_count: number;
    selection_fields: Record<string, any>;
    success: boolean;
    total_items_count: number;
  } | null | undefined;
  marginBottom?: string | number;
  marginTop?: string | number;
}

const DesignsInCollectionGrid = ({
                                   data,
                                   marginBottom,
                                   marginTop
                                 }: DesignsInCollectionGridProps) => {
  const navigate = useNavigate();

  // Extract the array of products from the data object
  const productArray = data?.data || [];

  // Filter to ensure valid items
  const filteredData = productArray.filter(item =>
    item && item.is_published !== false && item.sku && item.design
  );

  // If no data, show a message
  if (filteredData.length === 0) {
    return (
      <Box
        sx={{
          mt: marginTop,
          mb: marginBottom,
          ml: { xs: '5%', lg: '5rem' },
          mr: { xs: '5%', lg: '5rem' },
        }}
      >
        <Typography
          variant='h4'
          mb={2}
          fontWeight='400'
          fontSize={'56px'}
        >
          {`Designs in this collection`}
        </Typography>
        <Typography mt={2} mb={4} fontSize="18px" color="text.secondary">
          No designs found in this collection.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          mt: marginTop,
          mb: marginBottom,
          ml: { xs: '5%', lg: '5rem' },
          mr: { xs: '5%', lg: '5rem' },
        }}
      >
        <Typography
          variant='h4'
          mb={2}
          fontWeight='400'
          fontSize={'56px'}
        >
          {`Designs in this collection`}
        </Typography>

        <Typography mt={1} mb={2} fontWeight='bold'></Typography>

        <Grid container spacing={'12px'}>
          {filteredData
            .sort((a, b) =>
              (a.design || '').toLowerCase().localeCompare((b.design || '').toLowerCase())
            )
            .map((product, index) => {
              // Construct size string based on diameter or width x length
              const sizeString = product.diameter && product.diameter !== '0cm'
                ? product.diameter.trim()
                : `${product.width?.trim() || ''}x${product.length_cm?.trim() || ''}`;

              // Create URL with the product data
              const productUrl = `/item_group/${product.item_group}/application/${encodeURIComponent(
                product.application
              )}/collection/${encodeURIComponent(
                product.range
              )}/design/${encodeURIComponent(
                product.design
              )}/colour/${encodeURIComponent(
                product.colour
              )}?targetSize=${encodeURIComponent(sizeString)}&targetProduct=${encodeURIComponent(product.sku)}`;

              return (
                <Grid key={index} item xs={12} sm={6} lg={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      width: { xs: '100%', lg: '100%' },
                    }}
                  >
                    <Box
                      sx={{
                        cursor: 'pointer',
                        height: '350px',
                        width: '100%',
                        '&:hover img': {
                          transform: 'scale(1.03)',
                        },
                        overflow: 'hidden',
                      }}
                      onClick={() => {
                        navigate(productUrl);
                      }}
                    >
                      <img
                        src={product.primaryImageUrl}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                        }}
                        alt={`${product.design} in ${product.colour}`}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        width: '100%',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        mt={1}
                        fontWeight={'600'}
                        fontSize={'18px'}
                        textTransform={'capitalize'}
                        lineHeight={'27px'}
                      >
                        {product.design}
                      </Typography>
                      <Typography
                        fontWeight={'400'}
                        fontSize={'16px'}
                        textTransform={'capitalize'}
                        lineHeight={'27px'}
                      >
                        {product.colour}
                      </Typography>
                      {product.item_group === '004' && (
                        <Typography
                          mb={2}
                          fontWeight={'400'}
                          fontSize={'16px'}
                          lineHeight={'27px'}
                        >
                          {sizeString}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
        </Grid>
      </Box>
    </>
  );
};

export default DesignsInCollectionGrid;